import { Component, EventEmitter, Input, Output } from '@angular/core';
import {
  FieldType,
  FilterOperator,
  GROUPED_METADATA_FIELDS,
  MetadataFieldDef,
  MetadataFieldGroup,
  MetadataFilterDraft,
  OperatorOption,
  getOperatorsForFieldType,
} from '../../models/metadata-filter';

/**
 * A single row in the metadata filter builder.
 *
 * Emits a new (immutable) {@link MetadataFilterDraft} on every user change so
 * the parent can track validity and build the final search request.
 */
@Component({
  selector: 'metadata-filter-row',
  templateUrl: './metadata-filter-row.component.html',
  styleUrls: ['./metadata-filter-row.component.scss'],
})
export class MetadataFilterRowComponent {

  /** The draft provided by the parent — treated as read-only inside this component. */
  @Input() set draft(d: MetadataFilterDraft) {
    this.field    = d.field;
    this.operator = d.operator;
    this.value    = d.value;
    this.value2   = d.value2;
    this.availableOperators = d.field ? getOperatorsForFieldType(d.field.fieldType) : [];
  }

  /** Emits an updated draft whenever any field in the row changes. */
  @Output() draftChange = new EventEmitter<MetadataFilterDraft>();

  /** Emits when the user clicks the remove button. */
  @Output() remove = new EventEmitter<void>();

  // Expose enums to the template
  readonly FieldType = FieldType;
  readonly FilterOperator = FilterOperator;

  readonly groupedFields: MetadataFieldGroup[] = GROUPED_METADATA_FIELDS;

  // Local mutable state, synchronised from the @Input setter
  field:    MetadataFieldDef | null = null;
  operator: FilterOperator   | null = null;
  value:    unknown = null;
  value2:   unknown = null;

  availableOperators: OperatorOption[] = [];

  onFieldChange(): void {
    // Reset downstream selections when the field (and its fieldType) changes
    this.operator = null;
    this.value    = null;
    this.value2   = null;
    this.availableOperators = this.field ? getOperatorsForFieldType(this.field.fieldType) : [];
    this.emit();
  }

  onOperatorChange(): void {
    if (this.operator !== FilterOperator.BETWEEN) {
      this.value2 = null;
    }
    this.emit();
  }

  onValueChange(): void  { this.emit(); }
  onValue2Change(): void { this.emit(); }

  get isBetween(): boolean {
    return this.operator === FilterOperator.BETWEEN;
  }

  get selectedOperatorTooltip(): string {
    if (!this.operator) return 'Select operator';
    return this.availableOperators.find(o => o.value === this.operator)?.tooltip ?? this.operator;
  }

  private emit(): void {
    this.draftChange.emit({
      field:    this.field,
      operator: this.operator,
      value:    this.value,
      value2:   this.value2,
    });
  }
}

